/* global axios */

import Cookies from 'js-cookie';
import endPoints from './endPoints';
import {
  clearCookiesOnLogout,
  deleteIndexedDBOnLogout,
} from '../store/utils/api';
import { clearAxiosAuthHeaders } from '../helper/APIHelper';

export default {
  validityCheck() {
    const urlData = endPoints('validityCheck');
    return axios.get(urlData.url);
  },
  // ENGAGE: tear the session down completely before redirecting so a fast
  // re-login (e.g. after a role swap) can't inherit any stale state.
  // 1. await IndexedDB deletion (was fire-and-forget, racing the redirect)
  // 2. drop in-memory axios auth headers
  // 3. then clear cookies + navigate
  async logout() {
    const urlData = endPoints('logout');
    try {
      const response = await axios.delete(urlData.url, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      await deleteIndexedDBOnLogout();
      clearAxiosAuthHeaders(axios);
      const oidcLogoutUrl = response?.data?.oidc_logout_url;
      clearCookiesOnLogout(oidcLogoutUrl);
      return response;
    } catch (error) {
      // Best-effort cleanup even if the server-side logout failed, otherwise
      // the dashboard is stuck holding tokens the user thinks are gone.
      await deleteIndexedDBOnLogout().catch(() => {});
      clearAxiosAuthHeaders(axios);
      clearCookiesOnLogout();
      throw error;
    }
  },
  hasAuthCookie() {
    return !!Cookies.get('cw_d_session_info');
  },
  getAuthData() {
    if (this.hasAuthCookie()) {
      const savedAuthInfo = Cookies.get('cw_d_session_info');
      return JSON.parse(savedAuthInfo || '{}');
    }
    return false;
  },
  profileUpdate({ displayName, avatar, ...profileAttributes }) {
    const formData = new FormData();
    Object.keys(profileAttributes).forEach(key => {
      const hasValue = profileAttributes[key] === undefined;
      if (!hasValue) {
        formData.append(`profile[${key}]`, profileAttributes[key]);
      }
    });
    formData.append('profile[display_name]', displayName || '');
    if (avatar) {
      formData.append('profile[avatar]', avatar);
    }
    return axios.put(endPoints('profileUpdate').url, formData);
  },

  profilePasswordUpdate({ currentPassword, password, passwordConfirmation }) {
    return axios.put(endPoints('profileUpdate').url, {
      profile: {
        current_password: currentPassword,
        password,
        password_confirmation: passwordConfirmation,
      },
    });
  },

  updateUISettings({ uiSettings }) {
    return axios.put(endPoints('profileUpdate').url, {
      profile: { ui_settings: uiSettings },
    });
  },

  updateAvailability(availabilityData) {
    return axios.post(endPoints('availabilityUpdate').url, {
      profile: { ...availabilityData },
    });
  },

  updateAutoOffline(accountId, autoOffline = false) {
    return axios.post(endPoints('autoOffline').url, {
      profile: { account_id: accountId, auto_offline: autoOffline },
    });
  },

  deleteAvatar() {
    return axios.delete(endPoints('deleteAvatar').url);
  },

  resetPassword({ email }) {
    const urlData = endPoints('resetPassword');
    return axios.post(urlData.url, { email });
  },

  setActiveAccount({ accountId }) {
    const urlData = endPoints('setActiveAccount');
    return axios.put(urlData.url, {
      profile: {
        account_id: accountId,
      },
    });
  },
  resendConfirmation() {
    const urlData = endPoints('resendConfirmation');
    return axios.post(urlData.url);
  },
  resetAccessToken() {
    const urlData = endPoints('resetAccessToken');
    return axios.post(urlData.url);
  },
};
